import { useState, useEffect } from 'react';
import { SelectorResponse } from '@shared/interfaces/api/trackerRequest';
import { createSelector, updateSelector } from '@services/tracker/tracker.service';
import './SelectorModal.sass';

interface SelectorModalProps {
  projectId: string;
  selector: SelectorResponse | null;
  onClose: () => void;
  onSaved: () => void;
}

function SelectorModal({ projectId, selector, onClose, onSaved }: SelectorModalProps) {
  const [formData, setFormData] = useState({
    selector_type: 'css' as 'css' | 'xpath',
    selector_value: '',
    event_name: '',
    tracking_type: 'click' as 'click' | 'visibility' | 'hover' | 'form_submit' | 'value_change' | 'focus' | 'blur' | 'scroll_into_view',
    description: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selector) {
      setFormData({
        selector_type: selector.selector_type,
        selector_value: selector.selector_value,
        event_name: selector.event_name,
        tracking_type: selector.tracking_type,
        description: selector.description || '',
      });
    }
  }, [selector]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (selector) {
      // Update existing selector
      const response = await updateSelector(selector.id, {
        selector_value: formData.selector_value,
        event_name: formData.event_name,
        tracking_type: formData.tracking_type,
        description: formData.description,
      });

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }
    } else {
      // Create new selector
      const response = await createSelector(projectId, formData);

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(false);
    onSaved();
  };

  const isFormValid = () => {
    return (
      formData.selector_value.trim() !== '' &&
      formData.event_name.trim() !== ''
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{selector ? 'Edit Selector' : 'Add New Selector'}</h2>
          <button className="close-btn" onClick={onClose} type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="event_name">Event Name*</label>
            <input
              type="text"
              id="event_name"
              name="event_name"
              placeholder="e.g., button_click, form_submit"
              value={formData.event_name}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
            <span className="help-text">A unique name to identify this event</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="selector_type">Selector Type*</label>
              <select
                id="selector_type"
                name="selector_type"
                value={formData.selector_type}
                onChange={handleChange}
                disabled={isLoading || !!selector}
                required
              >
                <option value="css">CSS</option>
                <option value="xpath">XPath</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tracking_type">Tracking Type*</label>
              <select
                id="tracking_type"
                name="tracking_type"
                value={formData.tracking_type}
                onChange={handleChange}
                disabled={isLoading}
                required
              >
                <option value="click">Click</option>
                <option value="visibility">Visibility</option>
                <option value="hover">Hover</option>
                <option value="form_submit">Form Submit</option>
                <option value="value_change">Value Change</option>
                <option value="focus">Focus</option>
                <option value="blur">Blur</option>
                <option value="scroll_into_view">Scroll Into View</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="selector_value">Selector Value*</label>
            <input
              type="text"
              id="selector_value"
              name="selector_value"
              placeholder="e.g., #submit-button, .cta-button"
              value={formData.selector_value}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
            <span className="help-text">
              {formData.selector_type === 'css' ? 'CSS selector' : 'XPath expression'}
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              placeholder="Optional description for this selector"
              value={formData.description}
              onChange={handleChange}
              disabled={isLoading}
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading || !isFormValid()}
            >
              {isLoading ? 'Saving...' : selector ? 'Update Selector' : 'Add Selector'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SelectorModal;
